import { inject, reactive, shallowRef } from 'vue'
import type {
  Agency,
  AgencyGroup,
  AgencyStructureNode,
  GetAgencyStructureRequestParams
} from '@gat-ship/types'
import { isAgencyNode, isAgencyGroupNode, accessCodes } from '@gat-ship/types'
import { agencyStructureKey } from '@gat-ship/types/injection-keys'
import type {
  AgencyStructureCommands,
  AgencyStructureService
} from '@gat-ship/types/injection-keys'
import { useAgencyApiCalls } from '@gat-ship/ref-data'
import {
  useAgencySignalREvents,
  useCompanySignalREvents
} from '@gat-ship/signalr'
import { useUser } from '@gat-ship/user'
import { globalUtils } from '@gat-ship/utils'
import { mapAgencyNodeToAgency } from '../model/agency-structure-utils'

/**
 * Creates a reactive agency structure data source with caching,
 * request deduplication, and SignalR live-sync.
 */
export function useAgencyStructure(): AgencyStructureService {
  const {
    getAgencyRequest,
    getAgencyStructureRequest,
    addAgencyRequest,
    addAgencyGroupRequest,
    changeAgencyGroupNameRequest,
    moveAgencyGroupRequest,
    removeAgencyGroupRequest
  } = useAgencyApiCalls()

  const isLoading = reactive({
    agencyStructure: false
  })

  const user = useUser()

  const agencyStructure = shallowRef<AgencyStructureNode[]>([])

  let inFlightFetch: Promise<AgencyStructureNode[]> | undefined

  function setAgencyStructure(arr: AgencyStructureNode[]) {
    const sorted = arr.sort((a, b) => {
      if (a.treePath > b.treePath) return 1
      if (a.treePath < b.treePath) return -1
      return 0
    })
    agencyStructure.value = sorted
  }

  async function fetchAgencyStructure(
    params?: GetAgencyStructureRequestParams & {
      forceFetch?: boolean
      subscribeToEvents?: boolean
    }
  ) {
    if (!user.hasPermission(accessCodes.system.agency.viewAgencyStructure)) {
      throw new Error('User does not have permission to view agency structure')
    }
    if (!params?.forceFetch && agencyStructure.value.length > 0) {
      return agencyStructure.value
    }
    if (!params?.forceFetch && inFlightFetch) {
      return inFlightFetch
    }
    const includeInactive = params?.includeInactive ?? true
    isLoading.agencyStructure = true
    inFlightFetch = (async () => {
      try {
        const response = await getAgencyStructureRequest({ includeInactive })
        setAgencyStructure(response)
        if (params?.subscribeToEvents ?? true) {
          subscribeToEvents()
        }
        return response
      } finally {
        inFlightFetch = undefined
        isLoading.agencyStructure = false
      }
    })()
    return inFlightFetch
  }

  function findAgencyById(agencyId: string): Agency | undefined {
    const agencyNode = agencyStructure.value.find((i) => i.id === agencyId)
    if (agencyNode && isAgencyNode(agencyNode)) {
      return mapAgencyNodeToAgency(agencyNode)
    }
    return undefined
  }

  function updateAgencyGroup(agencyGroupId: string, newState: AgencyGroup) {
    if (agencyStructure.value) {
      const agencyStructureCopy = [...agencyStructure.value]
      const idx = agencyStructureCopy.findIndex((i) => i.id === agencyGroupId)
      if (idx >= 0) {
        agencyStructureCopy[idx] = {
          $type: 'agency-group',
          ...newState
        }
      }
      setAgencyStructure(agencyStructureCopy)
    }
  }

  function updateAgency(agencyId: string, agency: Agency) {
    const agencyStructureCopy = [...agencyStructure.value]
    const idx = agencyStructureCopy.findIndex((i) => i.id === agencyId)
    if (idx >= 0) {
      agencyStructureCopy[idx] = {
        $type: 'agency',
        ...agency
      }
    }
    setAgencyStructure(agencyStructureCopy)
  }

  function updateAgenciesCompanyDetails(
    companyCode: string,
    name: string,
    description: string | undefined
  ) {
    const agencyStructureCopy = [...agencyStructure.value]
    agencyStructureCopy.forEach((i) => {
      if (isAgencyNode(i)) {
        if (i.linkedCompany?.code === companyCode) {
          i.linkedCompany!.name = name
          i.linkedCompany!.description = description ?? undefined
        }
      }
    })
    setAgencyStructure(agencyStructureCopy)
  }

  const agencySignalREvents = useAgencySignalREvents({
    onAgencyAdded: () => {
      fetchAgencyStructure({ forceFetch: true })
    },
    onAgencyMoved: () => {
      fetchAgencyStructure({ forceFetch: true })
    },
    onAgencyDeactivated: () => {
      fetchAgencyStructure({ forceFetch: true })
    },
    onAgencyReactivated: () => {
      fetchAgencyStructure({ forceFetch: true })
    },
    onAgencyNameChanged: (event) => {
      const { agencyId, name, shortName } = event.message
      const agency = findAgencyById(agencyId)
      if (agency) {
        agency.name = name
        agency.shortName = shortName
        updateAgency(agencyId, agency)
      }
    },
    onAgencyContactInfoChanged: async (event) => {
      const { agencyId } = event.message
      try {
        await getAgencyRequest(agencyId)
      } catch {
        fetchAgencyStructure({ forceFetch: true })
      }
    },
    onAgencyLinkedToNewCompany: async (event) => {
      const { agencyId } = event.message
      try {
        await getAgencyRequest(agencyId)
      } catch {
        fetchAgencyStructure({ forceFetch: true })
      }
    },
    onAgencyGroupAdded: () => {
      fetchAgencyStructure({ forceFetch: true })
    },
    onAgencyGroupMoved: () => {
      fetchAgencyStructure({ forceFetch: true })
    },
    onAgencyGroupNameChanged: (event) => {
      const { agencyGroupId, name, shortName } = event.message
      const agencyGroupNode = agencyStructure.value.find(
        (i) => i.id === agencyGroupId
      )
      if (agencyGroupNode && isAgencyGroupNode(agencyGroupNode)) {
        agencyGroupNode.name = name
        agencyGroupNode.shortName = shortName
        updateAgencyGroup(agencyGroupId, agencyGroupNode)
      }
    },
    onAgencyGroupDeactivated: () => {
      fetchAgencyStructure({ forceFetch: true })
    }
  })

  const companySignalREvents = useCompanySignalREvents({
    onCompanyDetailsChanged: (event) => {
      const { code, name, description } = event.message
      updateAgenciesCompanyDetails(code, name, description ?? undefined)
    }
  })

  function unsubscribeFromEvents() {
    agencySignalREvents.unsubscribeFromAgencyEvents()
    companySignalREvents.unsubscribeFromCompanyEvents()
  }

  function subscribeToEvents() {
    agencySignalREvents.subscribeToAgencyEvents()
    companySignalREvents.subscribeToCompanyEvents()
    return () => {
      unsubscribeFromEvents()
    }
  }

  /**
   * Runs a structure mutation request, then force-refetches the tree. Wraps the
   * whole cycle in the shared `isLoading` flag so the write is observable.
   */
  async function runCommand(request: (commandId: string) => Promise<unknown>) {
    isLoading.agencyStructure = true
    try {
      await request(globalUtils.functions.uuid())
      await fetchAgencyStructure({ forceFetch: true })
    } finally {
      isLoading.agencyStructure = false
    }
  }

  const commands: AgencyStructureCommands = {
    addAgency: (payload) =>
      runCommand((commandId) => addAgencyRequest(payload, commandId)),
    addAgencyGroup: (payload) =>
      runCommand((commandId) => addAgencyGroupRequest(payload, commandId)),
    changeGroupName: (agencyGroupId, payload) =>
      runCommand((commandId) =>
        changeAgencyGroupNameRequest(agencyGroupId, payload, commandId)
      ),
    moveGroup: (agencyGroupId, payload) =>
      runCommand((commandId) =>
        moveAgencyGroupRequest(agencyGroupId, payload, commandId)
      ),
    removeGroup: (agencyGroupId) =>
      runCommand((commandId) =>
        removeAgencyGroupRequest(agencyGroupId, commandId)
      )
  }

  return {
    agencyStructure,
    fetchAgencyStructure,
    commands,
    clearData: (options?: { unsubscribeFromEvents?: boolean }) => {
      setAgencyStructure([])
      if (options?.unsubscribeFromEvents ?? true) {
        unsubscribeFromEvents()
      }
    },
    subscribeToEvents,
    unsubscribeFromEvents,
    isLoading
  }
}

/** Inject the agency structure context provided by a parent AgencyStructureProvider. */
export function useAgencyStructureContext() {
  return inject(agencyStructureKey, undefined)
}
